import React from "react";

import { useLazyQuery } from "@apollo/client";
import { createClient } from "graphql-ws";

import { API_URL_SUBSCRIPTION, JWT_TOKEN } from "../../constants/appConfig";
import {
  GET_PAGINATED_ESTIMATES_MESSAGES_QUERY,
  GET_PAGINATED_ORDERS_MESSAGES_QUERY,
} from "../../graphql/queries/common.query";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from "@ionic/react";

import "./chat.styles.css";
import { camera } from "ionicons/icons";
import AppButton from "../Buttons";
import { FileUpload, FileUploadPreview } from "../FileUpload";
import { getLocalStorage } from "../../lib/utils/storage.utils";
import { getNameFromUrl, getSignedUrl } from "../../lib/utils/common.utils";

interface IChatModal {
  isVisible: boolean;
  changeVisibility: any;
  activeData: any;
  roomId: any;
  category: "order" | "estimate";
  setHasUnreadMessages: any;
  orderNumber: any;
}

const ChatModal = (props: IChatModal) => {
  const {
    isVisible,
    changeVisibility,
    activeData,
    roomId,
    setHasUnreadMessages,
  } = props;

  const token = getLocalStorage(JWT_TOKEN);

  const [messages, setMessages] = React.useState<any[]>([]);
  const [chatMessage, setChatMessage] = React.useState<string>("");
  const [chatDocuments, setChatDocuments] = React.useState<any[]>([]);

  const client = createClient({
    url: `${API_URL_SUBSCRIPTION}/subscription`,
    connectionParams: async () => {
      return { token: token };
    },
  });

  const [getAllEstimateMessages] = useLazyQuery(
    GET_PAGINATED_ESTIMATES_MESSAGES_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
      onCompleted(data) {
        const result = data?.getPaginatedEstimateMessage?.estimateMessage;
        setMessages(result?.slice()?.reverse());
      },
    }
  );

  const [getAllOrderMessages] = useLazyQuery(
    GET_PAGINATED_ORDERS_MESSAGES_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
      onCompleted(data) {
        const result = data?.getPaginatedOrderMessage?.data;
        setMessages(result?.slice()?.reverse());
      },
    }
  );

  // Seding new chat
  const handleSendChat = async () => {
    if (props.category === "estimate") {
      handleEstimateChatSend();
    } else {
      handleOrderChatSend();
    }
  };

  const handleOrderChatSend = async () => {
    await new Promise((resolve, reject) => {
      const tempDocumentsArray =
        chatDocuments?.length > 0
          ? chatDocuments?.map((item) => item?.original)
          : null;

      client.subscribe(
        {
          query: `mutation(
            $message: String
            $documents: [String!]
            $orderId: ID!
            $roomId: String!
          ) {
            createOrderMessage(options: {
              message: $message,
              documents: $documents
              orderId: $orderId
              roomId: $roomId
            }) {
              errors {
                message
              }
              message {
                id
                orderId
                message
                documents
                customer {
                  id
                  lastName
                  firstName
                }
                admin {
                  id
                  lastName
                  firstName
                }
              }
            }
          }`,
          variables: {
            message: chatMessage ?? null,
            documents: tempDocumentsArray ?? null,
            orderId: activeData.id,
            roomId: props.roomId,
          },
        },
        {
          next: (data: unknown) => {
            if (data) {
              getAllOrderMessages({
                variables: {
                  order: "ASC",
                  field: "id",
                  offset: 0,
                  limit: 10,
                  orderId: activeData && activeData?.id,
                },
              });
              setChatMessage("");
              setChatDocuments([]);
            }
          },
          error: reject,
          complete: () => true,
        }
      );
    });
  };

  const handleEstimateChatSend = async () => {
    await new Promise((resolve, reject) => {
      const tempDocumentsArray =
        chatDocuments?.length > 0
          ? chatDocuments?.map((item) => item?.original)
          : null;

      client.subscribe(
        {
          query: `mutation(
            $message: String
            $documents: [String!]
            $estimateId: ID!
          ) {
            createEstimateMessage(options: {
              message: $message,
              documents: $documents
              estimateId: $estimateId
            }) {
              errors {
                message
              }
              estimateMessage {
                id
                estimateId
                message
                documents
                customer {
                  id
                  lastName
                  firstName
                }
                admin {
                  id
                  lastName
                  firstName
                }
              }
            }
          }`,
          variables: {
            message: chatMessage ?? null,
            documents: tempDocumentsArray ?? [],
            estimateId: activeData.id,
          },
        },
        {
          next: (data: unknown) => {
            if (data) {
              getAllEstimateMessages({
                variables: {
                  order: "ASC",
                  field: "id",
                  offset: 0,
                  limit: 10,
                  estimateId: activeData && activeData?.id,
                },
              });
              setChatMessage("");
              setChatDocuments([]);
            }
          },
          error: reject,
          complete: () => true,
        }
      );
    });
  };

  // Handling message read
  const handleChatRead = async (id: number) => {
    if (!id) return;
    if (props.category === "estimate") {
      handleEstimateChatRead(id);
    } else {
      handleOrderChatRead(id);
    }
  };

  const handleOrderChatRead = async (id: number) => {
    await new Promise((resolve, reject) => {
      client.subscribe(
        {
          query: `
          mutation {
            updateOrderMessageStatus(id: ${id}) {
              errors { message }
              message
            }
          }`,
        },
        {
          error: reject,
          next: () => true,
          complete: () => true,
        }
      );
    });
  };

  const handleEstimateChatRead = async (id: number) => {
    await new Promise((resolve, reject) => {
      client.subscribe(
        {
          query: `
          mutation {
            updateEstimateMessageStatus(id: ${id}) {
              errors {message }
              message
            }
          }`,
        },
        {
          error: reject,
          next: () => true,
          complete: () => true,
        }
      );
    });
  };

  // Handling subscription
  const handleSubscriptions = async () => {
    if (props.category === "estimate") {
      handleEstimateSubscription();
    } else {
      handleOrderSubscription();
    }
  };

  const handleOrderSubscription = async () => {
    await new Promise((resolve, reject) => {
      const orderId = activeData?.id;
      client.subscribe(
        {
          query: `subscription NormalSubscription {
            subscribeToOrderChat(topic: "ORDER-CHAT-${orderId}-${roomId}") {
              errors {
                message
              }
              message {
                id
                message
                documents
                customer {
                  id
                  lastName
                  firstName
                }
                admin {
                  id
                  lastName
                  firstName
                  image
                }
              }
            }
          }`,
        },
        {
          complete: () => true,
          next: () => {
            // if (!isVisible) setHasUnreadMessages(true);
            getAllOrderMessages({
              variables: {
                order: "ASC",
                field: "id",
                offset: 0,
                limit: 10,
                orderId: activeData?.id,
              },
            });
          },
          error: reject,
        }
      );
    });
  };

  const handleEstimateSubscription = async () => {
    await new Promise((resolve, reject) => {
      const estimateId = activeData?.id;
      client.subscribe(
        {
          query: `subscription NormalSubscription {
            subscribeToEstimateChat(topic: "ESTIMATE-${estimateId}-${roomId}") {
              errors {
                message
              }
              estimateMessage {
                id
                message
                documents
                customer {
                  id
                  lastName
                  firstName
                }
                admin {
                  id
                  lastName
                  firstName
                  image
                }
              }
            }
          }`,
        },
        {
          complete: () => true,
          next: () => {
            // if (!isVisible) setHasUnreadMessages(true);
            getAllEstimateMessages({
              variables: {
                order: "ASC",
                field: "id",
                offset: 0,
                limit: 10,
                estimateId: activeData?.id,
              },
            });
          },
          error: reject,
        }
      );
    });
  };

  const scrollToBottomOfModal = () => {
    // @ts-ignore
    setTimeout(() => {
      const chatContainer = document.getElementById("chat-container");
      const height = chatContainer?.scrollHeight;

      chatContainer &&
        chatContainer.scrollTo({
          top: height || 500,
          left: 0,
          behavior: "auto",
        });
    }, 120);
  };

  const handleDocClick = (item: string) => {
    getSignedUrl(item, true);
  };

  const removeDocument = (file: any) => {
    const otherFiles = chatDocuments.filter(
      (f: any) => f.original !== file.original
    );
    setChatDocuments(otherFiles);
  };

  const resetInput = () => {
    changeVisibility(false);
    setChatDocuments([]);
    setChatMessage("");
  };

  React.useEffect(() => {
    if (activeData && activeData.id && activeData.roomId) {
      handleSubscriptions();
      const commonFilters = {
        order: "ASC",
        field: "id",
        offset: 0,
        limit: 10,
      };
      if (props.category === "estimate") {
        getAllEstimateMessages({
          variables: {
            ...commonFilters,
            estimateId: activeData?.id,
          },
        });
      } else {
        getAllOrderMessages({
          variables: {
            ...commonFilters,
            orderId: activeData?.id,
          },
        });
      }
    }
  }, [activeData]);

  // Mark the last message as read
  React.useEffect(() => {
    if (!messages) return;
    scrollToBottomOfModal();

    const lastMessage = [...messages].reverse()[0];
    if (!lastMessage) return;
    if (lastMessage.customer) return;

    if (isVisible) {
      if (lastMessage.isSeen) return;
      handleChatRead(lastMessage?.id);
      setHasUnreadMessages(false);
    } else {
      setHasUnreadMessages(!lastMessage.isSeen);
    }
  }, [messages]);

  React.useEffect(() => {
    if (!isVisible) {
      setChatDocuments([]);
      setChatMessage("");
      return;
    }

    scrollToBottomOfModal();
    const lastMessage = [...messages].reverse()[0];
    if (!lastMessage) return;
    if (lastMessage.customer) return;

    handleChatRead(lastMessage.id);
    setHasUnreadMessages(false);
  }, [isVisible]);

  return (
    <IonModal
      isOpen={isVisible}
      onDidDismiss={() => changeVisibility(false)}
      cssClass="ion-chat-modal"
    >
      <IonHeader translucent>
        <IonToolbar className="chat-modal-header">
          <IonButtons collapse={true} slot="end">
            <IonButton onClick={() => changeVisibility(false)}>x</IonButton>
          </IonButtons>
          <IonTitle>
            <span>{props.orderNumber}</span>
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className={"chat-container"} id="chat-container">
          <div className={"chat-box-details"}>
            {messages &&
              messages?.map((message: any, index: number) => (
                <div key={index}>
                  {message?.customer && (
                    <div className="chat-me-wrapper">
                      <div className="chat-me-avatar">
                        <strong style={{ display: "block" }}>
                          {message.customer.firstName}{" "}
                          {message.customer.lastName}{" "}
                        </strong>

                        <div className="chat-me-message">
                          {!(
                            message?.message === "" ||
                            message?.message === "null"
                          ) && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <span className="chat-self">
                                {message?.message}
                              </span>
                            </div>
                          )}

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                            }}
                          >
                            {message?.documents &&
                              message?.documents?.[0] !== "" &&
                              message?.documents?.map(
                                (item: any, id: number) => (
                                  <React.Fragment key={id}>
                                    {item !== "null" && (
                                      <div
                                        className="chat-document-div"
                                        style={{
                                          marginLeft: 20,
                                          paddingTop: 5,
                                          paddingBottom: 5,
                                          cursor: "pointer",
                                          height: "100%",
                                        }}
                                      >
                                        <div
                                          onClick={() => handleDocClick(item)}
                                          style={{
                                            display: "flex",
                                          }}
                                        >
                                          <div
                                            style={{
                                              marginTop: 1,
                                              marginRight: 4,
                                            }}
                                          >
                                            <IonIcon
                                              slot="icon-only"
                                              icon={camera}
                                            />
                                          </div>
                                          <div>{getNameFromUrl(item)}</div>
                                        </div>
                                      </div>
                                    )}
                                  </React.Fragment>
                                )
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {message?.admin && (
                    <div>
                      <div style={{ paddingTop: 15 }}>
                        <strong>Admin </strong>
                      </div>

                      {!(
                        message?.message === "" || message?.message === "null"
                      ) && (
                        <div className="flex-justify-end">
                          <span className="chat-others mb-0">
                            {message?.message}
                          </span>
                        </div>
                      )}

                      <div className="flex-justify-end ms-2">
                        {message?.documents &&
                          message?.documents?.[0] !== "" &&
                          message?.documents?.map((item: any, id: number) => (
                            <React.Fragment key={id}>
                              {item !== "null" && (
                                <p className="chat-document-div">
                                  <span onClick={() => handleDocClick(item)}>
                                    <IonIcon slot="icon-only" icon={camera} />
                                    {getNameFromUrl(item)}
                                  </span>
                                </p>
                              )}
                            </React.Fragment>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>

          <div style={{ padding: "0 20px 10px 20px" }}>
            <FileUploadPreview
              files={chatDocuments}
              removeFile={removeDocument}
            />
          </div>

          <div className="chat-box-chat">
            <input
              className="chat-box-text-area"
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
            />
          </div>

          <div style={{ padding: 10 }}>
            <div>
              <FileUpload
                dragAndDrop
                formLess={{
                  files: chatDocuments,
                  setFiles: setChatDocuments,
                }}
                showImage={false}
              />
            </div>

            <div className="flex-justify-end mt-2 chat-buttons">
              <AppButton
                className={"primary me-2"}
                onClick={resetInput}
                buttonType="warning"
              >
                Chiudi
              </AppButton>

              <AppButton
                className={"success me-2"}
                onClick={() => handleSendChat()}
              >
                Invia
              </AppButton>
            </div>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default ChatModal;
